// src/utils/LiveChatSocket.js
// WebSocket client for live agent chat - connects to klonit service (port 5005)

import { KLONIT_WEBSOCKET_URL } from '../config';

const LIVE_AGENT_CHAT_PATH = '/live-agent-chat';

class LiveChatSocket {
  constructor() {
    this.socket = null;
    this.callbacks = new Map();
    this.registeredSessions = new Set();
    this.userId = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectInterval = 1000;
    this.connecting = false;
    this.connectionPromise = null;
    this.pendingRegistrations = [];
  }

  static getInstance() {
    if (!LiveChatSocket._instance) {
      LiveChatSocket._instance = new LiveChatSocket();
    }
    return LiveChatSocket._instance;
  }

  async initSocket() {
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
      if (this.socket.readyState === WebSocket.CONNECTING && this.connectionPromise) {
        await this.connectionPromise;
      }
      return;
    }

    if (this.connecting && this.connectionPromise) {
      return this.connectionPromise;
    }

    this.connecting = true;

    this.connectionPromise = new Promise((resolve, reject) => {
      const url = `${KLONIT_WEBSOCKET_URL}${LIVE_AGENT_CHAT_PATH}`;
      console.log('[LiveChatSocket] connecting to', url);

      try {
        this.socket = new WebSocket(url);
      } catch (error) {
        console.error('[LiveChatSocket] Failed to create WebSocket:', error);
        this.connecting = false;
        this.connectionPromise = null;
        reject(error);
        return;
      }

      const connectionTimeout = setTimeout(() => {
        if (this.socket?.readyState === WebSocket.CONNECTING) {
          console.error('[LiveChatSocket] Connection timeout');
          this.socket.close();
          this.connecting = false;
          this.connectionPromise = null;
          reject(new Error('Connection timeout'));
        }
      }, 10000);

      this.socket.onopen = () => {
        clearTimeout(connectionTimeout);
        console.log('[LiveChatSocket] connected');
        this.reconnectAttempts = 0;
        this.connecting = false;

        const pending = [...this.pendingRegistrations];
        this.pendingRegistrations = [];

        for (const sessionId of Array.from(this.registeredSessions)) {
          this._sendRegister(sessionId);
        }

        for (const { sessionId, userId } of pending) {
          this.userId = userId;
          this._sendRegister(sessionId);
        }

        resolve();
      };

      this.socket.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          console.log('[LiveChatSocket] Received message:', data);

          const sid = data.session_id;
          if (typeof sid === 'number') {
            const cbs = this.callbacks.get(sid) || [];
            for (const cb of cbs) {
              try {
                cb(data);
              } catch (e) {
                console.error('LiveChatSocket callback error', e);
              }
            }
          }

          // Also notify global listeners (session 0)
          if (typeof sid === 'number' && sid !== 0 && this.callbacks.has(0)) {
            const globals = this.callbacks.get(0) || [];
            for (const gcb of globals) {
              try {
                gcb(data);
              } catch (e) {
                console.error('LiveChatSocket global callback error', e);
              }
            }
          }
        } catch (e) {
          console.error('[LiveChatSocket] failed to parse message', e);
        }
      };

      this.socket.onerror = (err) => {
        clearTimeout(connectionTimeout);
        console.error('[LiveChatSocket] socket error', err);
        this.connecting = false;
        this.connectionPromise = null;
        reject(err);
        this.attemptReconnect();
      };

      this.socket.onclose = (ev) => {
        clearTimeout(connectionTimeout);
        console.log('[LiveChatSocket] socket closed', ev.code, ev.reason);
        this.socket = null;
        this.connecting = false;
        this.connectionPromise = null;
        this.attemptReconnect();
        resolve();
      };
    });

    return this.connectionPromise;
  }

  attemptReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const wait = this.reconnectInterval * Math.min(this.reconnectAttempts, 3);
      console.log(`[LiveChatSocket] attempting reconnect #${this.reconnectAttempts} in ${wait}ms`);
      setTimeout(() => {
        this.initSocket().catch((err) => {
          console.error('[LiveChatSocket] Reconnection failed:', err);
        });
      }, wait);
    } else {
      console.error('[LiveChatSocket] max reconnect attempts reached');
    }
  }

  _sendRegister(sessionId) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      console.warn('[LiveChatSocket] Cannot send register - socket not open');
      return;
    }
    const payload = {
      type: 'register',
      session_id: sessionId,
      user_id: this.userId,
    };
    console.log('[LiveChatSocket] send register:', payload);
    try {
      this.socket.send(JSON.stringify(payload));
    } catch (error) {
      console.error('[LiveChatSocket] Failed to send register:', error);
    }
  }

  _sendUnregister(sessionId) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      console.warn('[LiveChatSocket] Cannot send unregister - socket not open');
      return;
    }
    const payload = {
      type: 'unregister',
      session_id: sessionId,
      user_id: this.userId,
    };
    console.log('[LiveChatSocket] send unregister:', payload);
    try {
      this.socket.send(JSON.stringify(payload));
    } catch (error) {
      console.error('[LiveChatSocket] Failed to send unregister:', error);
    }
  }

  async subscribe(sessionId, userId, onMessage) {
    console.log(`[LiveChatSocket] Subscribing to session ${sessionId} for user ${userId}`);

    this.userId = userId;

    const arr = this.callbacks.get(sessionId) || [];
    arr.push(onMessage);
    this.callbacks.set(sessionId, arr);

    this.registeredSessions.add(sessionId);

    try {
      await this.initSocket();

      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        this._sendRegister(sessionId);
      } else {
        this.pendingRegistrations.push({ sessionId, userId });
      }
    } catch (error) {
      console.error('[LiveChatSocket] Failed to initialize socket during subscribe:', error);
    }
  }

  unsubscribe(sessionId, onMessage) {
    const arr = this.callbacks.get(sessionId) || [];

    if (onMessage) {
      const i = arr.indexOf(onMessage);
      if (i >= 0) arr.splice(i, 1);
    } else {
      arr.length = 0;
    }

    if (arr.length > 0) {
      this.callbacks.set(sessionId, arr);
    } else {
      this.callbacks.delete(sessionId);
      if (this.registeredSessions.has(sessionId)) {
        this.registeredSessions.delete(sessionId);
        try {
          this._sendUnregister(sessionId);
        } catch (e) {
          console.error('[LiveChatSocket] Error during unregister:', e);
        }
      }
    }
  }

  sendMessage(sessionId, senderId, message) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      console.error('Cannot send message, WebSocket is not connected.');
      this.initSocket()
        .then(() => {
          if (this.socket && this.socket.readyState === WebSocket.OPEN) {
            this.sendMessage(sessionId, senderId, message);
          }
        })
        .catch((err) => {
          console.error('Failed to reconnect for message send:', err);
        });
      return;
    }

    const payload = {
      type: 'message',
      session_id: sessionId,
      sender_id: senderId,
      message,
    };

    console.log('[LiveChatSocket] Sending message:', payload);

    try {
      this.socket.send(JSON.stringify(payload));
    } catch (error) {
      console.error('[LiveChatSocket] Failed to send message:', error);
    }
  }

  sendTypingStart(sessionId, senderId) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;

    const payload = {
      type: 'typing_start',
      session_id: sessionId,
      sender_id: senderId,
    };

    try {
      this.socket.send(JSON.stringify(payload));
    } catch (error) {
      console.error('[LiveChatSocket] Failed to send typing indicator:', error);
    }
  }

  sendSystem(sessionId, senderId, systemPayload) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      console.error('Cannot send system message, WebSocket is not connected.');
      return;
    }

    const payload = {
      type: 'system',
      session_id: sessionId,
      sender_id: senderId,
      ...systemPayload,
    };

    console.log('[LiveChatSocket] send system:', payload);

    try {
      this.socket.send(JSON.stringify(payload));
    } catch (error) {
      console.error('[LiveChatSocket] Failed to send system message:', error);
    }
  }

  sendAgentAssigned(sessionId, agentId, agentName, agentRole) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      console.error('Cannot send agent assignment, WebSocket is not connected.');
      return;
    }

    const payload = {
      type: 'system',
      event: 'agent_assigned',
      session_id: sessionId,
      sender_id: agentId,
      profile: {
        name: agentName,
        role: agentRole,
      },
    };

    console.log('[LiveChatSocket] Sending agent assignment:', payload);

    try {
      this.socket.send(JSON.stringify(payload));
    } catch (error) {
      console.error('[LiveChatSocket] Failed to send agent assignment:', error);
    }
  }

  async connect(sessionId, userId, onMessage) {
    await this.subscribe(sessionId, userId, onMessage);
  }

  disconnect(sessionId, onMessage) {
    if (typeof sessionId === 'number') {
      this.unsubscribe(sessionId, onMessage);
    } else {
      this.disconnectAll();
    }
  }

  disconnectAll() {
    try {
      if (this.socket) {
        this.socket.close();
      }
    } catch (e) {
      console.error('Error closing socket', e);
    }

    this.socket = null;
    this.connectionPromise = null;
    this.callbacks.clear();
    this.registeredSessions.clear();
    this.pendingRegistrations = [];
    this.userId = null;
    this.reconnectAttempts = 0;
    this.connecting = false;
  }

  isConnected() {
    return this.socket !== null && this.socket.readyState === WebSocket.OPEN;
  }
}

LiveChatSocket._instance = null;

export const liveChatSocket = LiveChatSocket.getInstance();
